import {Dialog} from 'primeng/dialog';

import {computed, inject, Injectable, Signal} from '@angular/core';
import {ActivatedRoute, Route, Router} from '@angular/router';
import {signalExt, WritableSignalExt} from '@shared/signals-extension';

import {GLOBAL_DIALOG_DEFAULT_CONFIG} from './config/global-dialog-default-config.const';
import {GlobalDialogConfig, RouteDataWithDialog} from './config/global-dialog.config.types';
import {GLOBAL_DIALOG_CONTROLLERS} from './global-dialog.providers';

@Injectable({
	providedIn: 'root',
})
export class GlobalDialoglService<T = any> {
	public static readonly GLOBAL_DIALOG_OUTLET_NAME: string = 'globalDialog';
	private static readonly PathWithOutletRegex: RegExp = new RegExp(`^.*\\(${GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME}:.*\\).*$`);
	private static readonly PathWithOutletReplaceRegex: RegExp = new RegExp(`\\(${GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME}:.*?\\)`, 'g');
	/* -------------------------------------------------------------------------------------------------------------- */
	private readonly controllers = inject(GLOBAL_DIALOG_CONTROLLERS);
	/* -------------------------------------------------------------------------------------------------------------- */
	private readonly router: Router = inject(Router);
	private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute); // to retrieve the current route data
	/* -------------------------------------------------------------------------------------------------------------- */
	private globalDialogRef: Dialog = null;

	/** The dialog configuration */
	private readonly config$: WritableSignalExt<GlobalDialogConfig<T>> = signalExt(GLOBAL_DIALOG_DEFAULT_CONFIG<T>());

	readonly #visible$: WritableSignalExt<boolean> = this.controllers.visible$;
	/** The visibility of the dialog */
	public readonly visible$: Signal<boolean> = computed(() => {
		return this.#visible$();
	});
	/* -------------------------------------------------------------------------------------------------------------- */
	constructor() {
		// listen to the close observable and close the dialog
		this.controllers.close$.subscribe(() => {
			if (this.visible$()) this.close();
		});
	}

	/* --------------------------------------------- Public Methods --------------------------------------------- */
	/**
	 * Set the global dialog reference to the service
	 * @note: this method should be called only once in the application
	 * @param globalDialogRef
	 */
	public __setGlobalDialogRef = (globalDialogRef: Dialog): void => {
		if (this.globalDialogRef) return;
		if (GlobalDialoglService.PathWithOutletRegex.test(location.pathname)) this.close(true);
		this.globalDialogRef = globalDialogRef;
	};

	/**
	 * Open the dialog
	 * @param dialogPath
	 * @param config
	 */
	public open = (dialogPath: string, config?: GlobalDialogConfig<T>): void => {
		this._applyConfig(dialogPath, config);

		const path: string[] = this.generatePath(dialogPath, this.config$().inputsData);
		this.router
			.navigate([{outlets: {[GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME]: path}}], {
				relativeTo: this.activatedRoute,
				replaceUrl: true,
			})
			.then(() => {
				this.#visible$.set(true);
			});
	};

	/**
	 * Close the dialog
	 * @returns an observable that emits true when the dialog is closed
	 * or false when the dialog is already closed
	 * or undefined when the dialog is not visible
	 */
	public close = (force = false): void => {
		this.#visible$.set(false);
		// navigate to the same route without the dialog outlet => without `(GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME)`
		const currentRouteWithoutDialogOutlet = location.pathname.replace(GlobalDialoglService.PathWithOutletReplaceRegex, '');
		if (force && !this.visible$()) {
			this.router.navigate([currentRouteWithoutDialogOutlet], {replaceUrl: true});
		} else {
			this.router.navigate([{outlets: {[GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME]: null}}], {
				relativeTo: this.activatedRoute,
				replaceUrl: true,
			});
		}
	};

	/* --------------------------------------------  Private Methods -------------------------------------------- */
	/**
	 * Apply the provided configs to the current configs
	 * @param dialogPath
	 * @param openConfig
	 * @algorithm
	 * Config priority:
	 * 1. open(...) method configs
	 * 2. route data configs
	 * 3. default configs
	 */
	private _applyConfig = (dialogPath: string, openConfig: GlobalDialogConfig<T>): void => {
		openConfig = openConfig || {};
		const routeDataConfig = this.getRouteData(dialogPath) || {};
		const updatedConfig = GLOBAL_DIALOG_DEFAULT_CONFIG<T>({...routeDataConfig, ...openConfig});
		this.config$.set(updatedConfig);
		if (this.globalDialogRef) {
			Object.assign(this.globalDialogRef, updatedConfig);
		}
	};

	/**
	 * Get the dialog data from the route data
	 * @param path
	 * @private
	 */
	private getRouteData(path: string): RouteDataWithDialog<T> {
		const route: Route = this.router.config.find(route => {
			if (route.outlet !== GlobalDialoglService.GLOBAL_DIALOG_OUTLET_NAME) return false;
			const pathWithoutParamsRegex = route.path.replace(/\/:[^/]+/g, '');
			return path === pathWithoutParamsRegex;
		});
		if (!route?.data) return null;
		if (!Reflect.has(route.data, 'dialog')) return null;
		return route.data.dialog as RouteDataWithDialog<T>;
	}

	private generatePath(dialogPath: string, inputsData: Record<keyof T, T[keyof T]>): string[] {
		const path: string[] = [dialogPath];
		if (!inputsData) return path;
		Object.values(inputsData).forEach(value => path.push(value.toString()));
		return path;
	}
}
